import React, {useMemo} from 'react';
import {Box, Stack, Typography, List, ListItemButton, Chip, InputBase, Skeleton, Select, MenuItem, Tooltip} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {useNavigate} from 'react-router-dom';
import {HugeiconsIcon} from '@hugeicons/react';
import {Search01Icon, DatabaseIcon, Wrench01Icon, LockIcon, LockOpenIcon} from '@hugeicons/core-free-icons';
import {SubProjectOption} from '../../SubProjectSelect';
import {serversAtom, selectedServerAtom, searchQueryAtom, statusFilterAtom, mcpLoadingAtom, flowUidAtom, projectAtom, subProjectAtom} from '../atoms';

const controlSx = {height: 36, display: "flex", alignItems: "center", gap: 1, px: 1.25, borderRadius: "10px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13};

export const MCPServerListPanelNew = ({ onAddClick, onRefresh }) => {
  const [servers] = useAtom(serversAtom);
  const [selectedServer, setSelectedServer] = useAtom(selectedServerAtom);
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [statusFilter, setStatusFilter] = useAtom(statusFilterAtom);
  const [loading] = useAtom(mcpLoadingAtom);
  const flowUid = useAtomValue(flowUidAtom);
  const project = useAtomValue(projectAtom);
  const subProject = useAtomValue(subProjectAtom);
  const navigate = useNavigate();

  const subProjects = project?.subProjects || [];

  const handleChangeSubProject = (id) => {
    if (!project?.id) return;
    navigate(`/registry-project/${project.id}/${id}/mcp-registry`);
  };

  const scopedServers = useMemo(() =>
    flowUid ? servers.filter(server => server.flow_uid === flowUid) : servers,
    [servers, flowUid]);

  const filteredServers = useMemo(() => {
    return scopedServers.filter((server) => {
      const matchesSearch = server.name?.toLowerCase().includes(searchQuery.toLowerCase()) || server.endpoint?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'All' || (statusFilter === 'Active' && server.active) || (statusFilter === 'Inactive' && !server.active);
      return matchesSearch && matchesStatus;
    });
  }, [scopedServers, searchQuery, statusFilter]);

  return (
    <Stack sx={{height: '100%', minHeight: 0, p: 3, gap: 2.5, display: 'flex', flexDirection: 'column'}}>
      <Box>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>REGISTRY</Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{mt: 0.5}}>
          <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D"}}>Servers</Typography>
          {subProjects.length > 0 && subProject && (
            <Select
              variant="standard"
              disableUnderline
              value={String(subProject.id)}
              onChange={(e) => handleChangeSubProject(e.target.value)}
              renderValue={() => <SubProjectOption subProject={subProject}/>}
              inputProps={{"aria-label": "Sub project"}}
              sx={{
                maxWidth: 170, borderRadius: "999px", px: 1.25, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)",
                "& .MuiSelect-select": {py: 0.5, pr: "22px !important", display: "flex", alignItems: "center"},
                "& .MuiSelect-icon": {color: "#4B55C9"},
              }}
              MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
            >
              {subProjects.map(item => (
                <MenuItem key={item.id} value={String(item.id)} dense>
                  <SubProjectOption subProject={item}/>
                </MenuItem>
              ))}
            </Select>
          )}
        </Stack>
        <Typography sx={{fontSize: 13, color: "#6B6B80", mt: 0.5}}>{scopedServers.length} total · {scopedServers.filter(s => s.active).length} active</Typography>
      </Box>

      <Box sx={{...controlSx}}>
        <HugeiconsIcon icon={Search01Icon} size={16}/>
        <InputBase placeholder="Search servers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} inputProps={{"aria-label": "Search servers"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}} />
      </Box>

      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {['All', 'Active', 'Inactive'].map((status) => (
          <Chip
            key={status}
            label={status.toUpperCase()}
            size="small"
            onClick={() => setStatusFilter(status)}
            sx={{
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '0.04em',
              height: 28,
              border: 'none',
              bgcolor: statusFilter === status ? 'rgba(132, 145, 247, 0.14)' : '#FAFAFD',
              color: statusFilter === status ? '#4B55C9' : '#6B6B80',
              '&:hover': {bgcolor: statusFilter === status ? 'rgba(132, 145, 247, 0.2)' : '#ECECF3'},
            }}
          />
        ))}
      </Stack>

      <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', mr: -1, pr: 1}}>
        {loading && scopedServers.length === 0 ? (
          <Stack gap={1}>
            {Array.from({length: 5}).map((_, index) => (
              <Box key={index} sx={{p: 1.25, borderRadius: '10px'}}>
                <Skeleton height={18} width="60%" sx={{borderRadius: '6px'}}/>
                <Skeleton height={14} width="85%" sx={{borderRadius: '6px', mt: 0.5}}/>
              </Box>
            ))}
          </Stack>
        ) : filteredServers.length > 0 ? (
          <List sx={{p: 0, gap: 1, display: 'flex', flexDirection: 'column'}}>
            {filteredServers.map((server) => {
              const isSecure = server.auth_token === true;
              const toolCount = server.tools?.length || 0;
              return (
                <ListItemButton key={server.id} selected={selectedServer?.id === server.id} onClick={() => setSelectedServer(server)} sx={{p: 1.25, borderRadius: '10px', backgroundColor: 'transparent', transition: 'background-color 0.2s ease', '&:hover': {backgroundColor: '#F2F2F8'}, '&.Mui-selected': {backgroundColor: 'rgba(132, 145, 247, 0.08)'}, '&.Mui-selected:hover': {backgroundColor: 'rgba(132, 145, 247, 0.1)'}}} >
                  <Box sx={{width: '100%'}}>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Box sx={{
                        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                        bgcolor: server.active ? '#2F9A6A' : '#DC2626',
                      }}/>
                      <Typography sx={{fontWeight: 700, fontSize: '13px', color: '#23264D', flex: 1}} noWrap>{server.name}</Typography>
                    </Stack>
                    <Typography sx={{fontSize: '12px', color: '#8A8AA0', mt: 0.25}} noWrap>{server.endpoint}</Typography>
                    <Stack direction="row" alignItems="center" gap={1.25} sx={{mt: 0.5}}>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <HugeiconsIcon icon={Wrench01Icon} size={12} color={"#8A8AA0"}/>
                        <Typography sx={{fontSize: '11px', color: '#8A8AA0'}}>{toolCount} tool{toolCount === 1 ? '' : 's'}</Typography>
                      </Stack>
                      <Tooltip title={isSecure ? "Connection is secure" : "Connection may not be secure"}>
                        <Stack direction="row" alignItems="center" gap={0.5} sx={{color: isSecure ? '#2F9A6A' : '#D9A13A'}}>
                          <HugeiconsIcon icon={isSecure ? LockIcon : LockOpenIcon} size={12}/>
                          <Typography sx={{fontSize: '11px', fontWeight: 600}}>{isSecure ? 'Secure' : 'Insecure'}</Typography>
                        </Stack>
                      </Tooltip>
                    </Stack>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        ) : (
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6, px: 2, textAlign: 'center'}}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '14px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#8491F7',
            }}>
              <HugeiconsIcon icon={scopedServers.length === 0 ? DatabaseIcon : Search01Icon} size={22}/>
            </Box>
            <Box>
              <Typography sx={{fontSize: 14, fontWeight: 700, color: '#23264D'}}>
                {scopedServers.length === 0 ? 'No servers configured' : 'No servers match'}
              </Typography>
              <Typography sx={{fontSize: 12, color: '#8A8AA0', mt: 0.5}}>
                {scopedServers.length === 0 ? 'Add an MCP server to get started' : 'Try a different search or filter'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Stack>
  );
};
